/**
 * TGTP Product Quantity Validation
 *
 * 商品詳細ページの quantity selector の入力値と、購入上限
 * (product / variant の purchase_limit) および在庫数を突き合わせて
 * buy button を動的に disable する。
 *
 * 在庫上限は「在庫追跡ON かつ 在庫切れ時に販売しない (deny)」の variant のみ
 * data-variant-inventory-map に出力される (Liquid 側で判定)。マップに無い
 * variant は無制限として扱う。
 *
 * 期待する DOM:
 *   <div class="tgtp-product-buy-button"
 *        data-button-state="available"
 *        data-max-quantity="3"               (商品単位の上限 / 任意)
 *        data-max-variant-quantity="2"       (バリエーション単位の上限 / 任意)
 *        data-cart-qty="0"                   (カート内のこの商品の合計数)
 *        data-variant-cart-qty-map='{"123":0,"456":1}'
 *        data-variant-inventory-map='{"123":10}' (在庫上限 / 任意)
 *        data-current-variant-id="123"
 *        data-exceed-label="購入可能点数を超えています"
 *        data-stock-exceed-label="在庫数を超えています">
 *     <button class="tgtp-product-buy-btn" data-original-label="カートに入れる">
 *       <span class="tgtp-product-buy-btn__text">カートに入れる</span>
 *     </button>
 *   </div>
 *
 *   <div class="tgtp-product-quantity">
 *     <div class="tgtp-quantity-stepper">
 *       <input class="tgtp-quantity-stepper__input" type="number" value="1" min="1">
 *     </div>
 *   </div>
 *
 * 動作方針:
 * - data-button-state が "available" 以外の場合は何もしない
 *   (Liquid 側で既に売切れ／販売停止等で disabled になっている状態を
 *    JS で勝手に上書きしない)。
 * - quantity input の input / change イベントで再評価。
 * - バリエーション変更 → buy-button が Section Rendering API で
 *   再レンダリング → section が `tgtp:section-refreshed` を発火 → 再 init。
 * - 超過時: button.disabled = true, label を data-exceed-label に置換。
 * - 復帰時: button.disabled = false, label を data-original-label に戻す。
 */
(() => {
  const SECTION_SELECTOR = ".tgtp-main-product";
  const WRAPPER_SELECTOR = ".tgtp-product-buy-button";
  const BUTTON_SELECTOR = ".tgtp-product-buy-btn";
  const LABEL_SELECTOR = ".tgtp-product-buy-btn__text";
  const INPUT_SELECTOR = ".tgtp-product-quantity .tgtp-quantity-stepper__input";

  /** @returns {number | null} */
  const parseIntOrNull = (v) => {
    if (v === undefined || v === null || v === "") return null;
    const n = Number.parseInt(v, 10);
    return Number.isNaN(n) ? null : n;
  };

  const safeJsonParse = (s) => {
    try {
      return JSON.parse(s || "{}");
    } catch {
      return {};
    }
  };

  /**
   * 現在の buy-button wrapper を起点に、quantity 監視を仕掛けて評価する。
   * 評価のたびに DOM の data-button-state を再チェックするので、
   * 別経路で state が available 以外に変わったら自然に no-op になる。
   */
  const attach = (section) => {
    const wrapper = section.querySelector(WRAPPER_SELECTOR);
    if (!wrapper) return;
    if (wrapper.dataset.buttonState !== "available") return;

    const btn = wrapper.querySelector(BUTTON_SELECTOR);
    if (!btn) return;

    const labelEl = btn.querySelector(LABEL_SELECTOR) ?? btn;
    const input = section.querySelector(INPUT_SELECTOR);

    const maxQty = parseIntOrNull(wrapper.dataset.maxQuantity);
    const maxVariantQty = parseIntOrNull(wrapper.dataset.maxVariantQuantity);
    const variantInventoryMap = safeJsonParse(wrapper.dataset.variantInventoryMap);
    const hasInventoryLimit = Object.keys(variantInventoryMap).length > 0;
    if (maxQty === null && maxVariantQty === null && !hasInventoryLimit) return;

    const cartQty = parseIntOrNull(wrapper.dataset.cartQty) ?? 0;
    const variantCartQtyMap = safeJsonParse(wrapper.dataset.variantCartQtyMap);
    const exceedLabel =
      wrapper.dataset.exceedLabel || "購入可能点数を超えています";
    const stockExceedLabel =
      wrapper.dataset.stockExceedLabel || "在庫数を超えています";
    const originalLabel = btn.dataset.originalLabel ?? labelEl.textContent;

    const currentVariantCartQty = () => {
      const vid = wrapper.dataset.currentVariantId;
      if (!vid) return 0;
      return parseIntOrNull(variantCartQtyMap[vid]) ?? 0;
    };

    // 現在の variant の在庫上限。マップに無い variant は無制限 (null)。
    const currentVariantInventory = () => {
      const vid = wrapper.dataset.currentVariantId;
      if (!vid) return null;
      return parseIntOrNull(variantInventoryMap[vid]);
    };

    // 超過理由を返す。null=超過なし / "stock"=在庫超過 / "limit"=購入上限超過。
    // 在庫はハードな制約なので最優先で判定する。
    const exceedReason = (selectedQty) => {
      const inventory = currentVariantInventory();
      if (
        inventory !== null &&
        currentVariantCartQty() + selectedQty > inventory
      ) {
        return "stock";
      }
      if (maxQty !== null && cartQty + selectedQty > maxQty) return "limit";
      if (
        maxVariantQty !== null &&
        currentVariantCartQty() + selectedQty > maxVariantQty
      ) {
        return "limit";
      }
      return null;
    };

    const evaluate = () => {
      if (wrapper.dataset.buttonState !== "available") return;

      const raw = input ? Number.parseInt(input.value, 10) : 1;
      const selectedQty = Number.isNaN(raw) || raw < 1 ? 1 : raw;

      const reason = exceedReason(selectedQty);
      if (reason) {
        btn.disabled = true;
        labelEl.textContent = reason === "stock" ? stockExceedLabel : exceedLabel;
        wrapper.dataset.quantityExceeded = "true";
      } else {
        btn.disabled = false;
        labelEl.textContent = originalLabel;
        delete wrapper.dataset.quantityExceeded;
      }
    };

    // wrapper は section-refreshed のたびに置き換わるので、
    // listener は input (quantity 側) と wrapper 自身 (variant 切替前の同期用) に張る。
    if (input) {
      input.addEventListener("input", evaluate);
      input.addEventListener("change", evaluate);
    }
    wrapper.addEventListener("tgtp:variant-change", evaluate);

    evaluate();
  };

  const init = () => {
    const section = document.querySelector(SECTION_SELECTOR);
    if (!section) return;

    // section レベルの一度きりの listener を仕掛ける
    if (!section.dataset.tgtpQuantityValidationInitialized) {
      section.dataset.tgtpQuantityValidationInitialized = "1";
      section.addEventListener("tgtp:section-refreshed", () => attach(section));
    }

    attach(section);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
